// The sell-speed risk meter (decision D8) and the "Nd to median sale" tag.
//
// Risk meter. The tier (RiskTier: Green / Yellow / Red) is cut on the model's
// RAW P(critical) at calibrated, per-model thresholds (sell_speed/score.py,
// default 0.33 / 0.60), while the % shown (LadenhueterRisk) is the isotonic-
// calibrated probability. Isotonic calibration is monotone, so on the shown %
// scale the tiers still occupy three consecutive zones; their borders are not
// 33 / 60 though. The borders are read off the scored units the page already
// holds: the edge between two tiers lies between the highest % of the lower
// tier and the lowest % of the higher one. Equal thirds (the prototype) would
// put e.g. a 62% Red unit in "Medium". When the inventory cannot tell (no
// unit of a neighbouring tier), the model's default cuts are used.
//
// The marker is always drawn inside the unit's own tier zone, so the meter
// can never contradict the tier pill next to it.

export const DEFAULT_RISK_CUTS = Object.freeze([33, 60]);
const TIERS = ['Green', 'Yellow', 'Red'];

const pct = (u) => (typeof u?.LadenhueterRisk === 'number' && Number.isFinite(u.LadenhueterRisk) ? u.LadenhueterRisk : null);

const edge = (lowerMax, upperMin, fallback) => {
    if (lowerMax == null && upperMin == null) return fallback;
    if (lowerMax == null) return upperMin;
    if (upperMin == null) return lowerMax;
    return (lowerMax + upperMin) / 2;
};

/** [green|yellow edge, yellow|red edge] on the shown % scale (0–100). */
export function calibratedRiskCuts(units) {
    const range = { Green: [], Yellow: [], Red: [] };
    (units || []).forEach((u) => {
        const p = pct(u);
        if (p != null && range[u.RiskTier]) range[u.RiskTier].push(p);
    });
    const max = (list) => (list.length ? Math.max(...list) : null);
    const min = (list) => (list.length ? Math.min(...list) : null);
    let g = edge(max(range.Green), min(range.Yellow) ?? min(range.Red), DEFAULT_RISK_CUTS[0]);
    let y = edge(max(range.Yellow) ?? max(range.Green), min(range.Red), DEFAULT_RISK_CUTS[1]);
    // Keep the zones ordered and visible.
    g = Math.min(Math.max(g, 1), 98);
    y = Math.min(Math.max(y, g + 1), 99);
    return [Math.round(g * 10) / 10, Math.round(y * 10) / 10];
}

/**
 * The meter for a unit: the zone borders and the marker (0–100), or null for
 * an unscored unit. The marker sits at the unit's % clamped into its tier's
 * zone (1 pt inside the border).
 */
export function riskMeter(unit, units) {
    const tierIndex = TIERS.indexOf(unit?.RiskTier);
    if (tierIndex === -1) return null;
    const cuts = calibratedRiskCuts(units);
    const zones = [[0, cuts[0]], [cuts[0], cuts[1]], [cuts[1], 100]];
    const [lo, hi] = zones[tierIndex];
    const p = pct(unit);
    const inside = (v) => Math.min(Math.max(v, lo + (tierIndex === 0 ? 0 : 1)), hi - (tierIndex === 2 ? 0 : 1));
    const marker = p == null ? (lo + hi) / 2 : inside(p);
    return { cuts, marker: Math.round(marker * 10) / 10, tierIndex };
}

/**
 * Days until the median sale, from the existing survival curve: the unit's
 * MedianDays when the backend sent it, otherwise where the monthly curve
 * (SurvivalCurve[i] = P(still unsold after month i+1)) crosses 50%, linearly
 * interpolated and converted at 30.44 days a month. Null when neither exists
 * or the curve never reaches 50% within its horizon.
 */
export function daysToMedianSale(unit) {
    if (typeof unit?.MedianDays === 'number' && Number.isFinite(unit.MedianDays)) return Math.round(unit.MedianDays);
    const curve = Array.isArray(unit?.SurvivalCurve) ? unit.SurvivalCurve : null;
    if (!curve || !curve.length) return null;
    const points = [1, ...curve]; // month 0 = still unsold for sure
    for (let i = 1; i < points.length; i += 1) {
        const a = points[i - 1];
        const b = points[i];
        if (typeof b === 'number' && b <= 0.5 && a > 0.5) {
            const frac = (a - 0.5) / (a - b);
            return Math.round((i - 1 + frac) * 30.44);
        }
    }
    return null;
}
